import React, { useState } from 'react';
import { Grid, Modal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import componentConstants from '../../../core-utils/messages/componentConstants';
import NewEditDuration, {
  CurrentDates,
  InitialDates,
} from '../../molecules/NewEditDuration';
import { CSSProperties } from '@mui/material/styles';
import { Button } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import ReportsCohortItem from '../../atoms/ReportsCohortItem';
import { Close } from '@HomaHealth/ui-component-library';
import Info from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';

export interface CreateNewModalCoreComponentProps {
  // modal props
  open: boolean;
  handleClose: () => any;

  // content props
  title: string;
  subTitle: [string, CSSProperties | undefined][];
  // type: 'Dashboard' | 'Reports' | 'InternalReports';
  reportCreateNewLabelFields: React.JSX.Element;

  // date filter props
  isYearSelection: boolean;
  startDateUseState?: [string, React.Dispatch<React.SetStateAction<string>>];
  endDateUseState?: [string, React.Dispatch<React.SetStateAction<string>>];
  showDuration?: boolean;
  initialDates?: InitialDates;
  currentDates?: CurrentDates;

  // cohort values and functions
  cohortState: [
    ICohortDetails[],
    React.Dispatch<React.SetStateAction<ICohortDetails[]>>,
  ];
  propertyDropDownOptions: string[][];
  categoryDropDownOptions: string[];
  // subCategoryDropDownOptions?: string[][];

  conditionDropDownOptions: string[][];
  cohortSelectionsValues: string[];
  handleAddCohort: () => any;
   
  handleDeleteCohort: (index: number) => any;

  // save and cancel buttons
  handleDownload: () => any;
  isSaveButtonDisabled?: boolean;
  handleOnClickAddCohort: () => any;
  showAddCohorts: boolean;
  infoGrid?: React.JSX.Element;
  isEditCohorts?: boolean;
  emailScheduler?: React.ReactNode;

  resetDates?: boolean;
}

export interface Must {
  title: string;
  subTitle: string;
  fields: string[];
  cohortStates: string[];
}

export interface ICohortDetails {
  id: number;
  category: string;
  property: string;
  multipleProperties?: string[];
  dropdownSelectionTypeIndex: number;
  condition: string;
  results?: string;
  percentage?: string;
  andOrCondition?: string;
  propertiesPageNumber: number;
  totalPagesForProperties: number;
  subCategory?: string;
}

export const SubtitleBoldTypographyProps: CSSProperties = {
  color: patientAccessTheme.palette.text.highemphasis,
  fontWeight: 500,
  margin: '0px 4px',
};

export const EditDurationStyles = {
  mainComponent: {
    backgroundColor: 'inherit',
    boxShadow: 'none',
    border: 'none',
    width: 'auto ',
    height: 'auto',
  },
  dateContainerStyles: {
    marginLeft: '0px',
  },
  infoStyles: {
    marginLeft: '0px',
    marginBottom: '20px',
  },
};

const CreateNewModalCoreComponent: React.FC<CreateNewModalCoreComponentProps> = ({
  open,
  handleClose,
  startDateUseState,
  endDateUseState,
  isYearSelection,
  handleDownload,
  title,
  subTitle,
  reportCreateNewLabelFields,
  propertyDropDownOptions,
  categoryDropDownOptions,
  // subCategoryDropDownOptions,
  conditionDropDownOptions,
  cohortSelectionsValues,
  isSaveButtonDisabled,
  showDuration,
  initialDates,
  currentDates,
  handleOnClickAddCohort,
  showAddCohorts,
  infoGrid,
  isEditCohorts,
  emailScheduler,
  resetDates,
}) => {
  // styles
  const useStyles = makeStyles()({
    root: {
      width: '720px',
      height: 'fit-content',
      maxHeight: '1000px',
      backgroundColor: patientAccessTheme.palette.status.white,
      borderRadius: '8px',
      padding: '24px 32px 20px 32px',
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      flexDirection: 'column',
    },
    mainFieldsContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      marginTop: '16px',
      boxSizing: 'border-box',
      width: '656px',
      background: '#FBFBFE',
      border: `1px solid ${patientAccessTheme.palette.grey[100]}`,
      borderRadius: '8px',
      padding: '20px 18px',
      marginBottom: '16px',
    },
    textFieldsContainer: {
      marginBottom: '16px',
      width: 'fit-content',
    },
    cohortDetailsContainer: {
      overflowY: 'auto',
      ...scrollBarStyles,
    },
    cohortItem: {
      width: '656px',
      height: '170px',
      background: '#FBFBFE',
      borderRadius: '8px',
      marginBottom: '16px',
      padding: '16px 20px',
    },
    cancelButton: {
      padding: '9px 16px',
      width: '102px',
      height: '36px',
      border: '1px solid ' + patientAccessTheme.palette.primary[500],
      borderRadius: '4px',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 500,
    },
    saveButton: {
      padding: '9px 16px',
      width: '102px',
      height: '36px',
      border:
        '1px solid ' + isSaveButtonDisabled
          ? patientAccessTheme.palette.grey[500]
          : patientAccessTheme.palette.primary[500],
      borderRadius: '4px',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 500,
    },
    infoItem: {
      background: patientAccessTheme.palette.AIQChips?.infoBackground,
      border: '1px solid ' + patientAccessTheme.palette.grey[300],
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      height: '37px',
      width: '616px',
    },
    clickHere: {
      textDecoration:
        'underline 2px dashed ' + patientAccessTheme.palette.primary[500],
      textUnderlineOffset: '4px',
    },
    addCohortInfo: {
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'break-spaces',
    },
  });
  const { classes } = useStyles();

  //constants
  const constants = componentConstants.ORGANISMS.REPORTS_CREATE_NEW_MODAL;

  // Cohorts
  const [cohortDetailsList, setCohortDetailsList] = useState<ICohortDetails[]>(
    [],
  );

  //functions
  const getSubtitle = () => {
    return (
      <Grid container>
        {subTitle.map(([value, typographyProperties], index: number) => {
          return (
            <Grid key={index}>
              <Typography
                style={{
                  fontSize: '14px',
                  lineHeight: '25px',
                  color: patientAccessTheme.palette.text.mediumemphasis,
                  ...typographyProperties,
                }}
              >
                {value}
              </Typography>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const handleAddCohort = () => {
    const cohort: ICohortDetails = {
      id: cohortDetailsList.length + 1,
      category: '',
      condition: '',
      property: '',
      // subCategory: '',
      dropdownSelectionTypeIndex: 0,
      propertiesPageNumber: 0,
      totalPagesForProperties: 0,
    };
    setCohortDetailsList((prev) => [...prev, cohort]);
  };

  const handleDeleteCohort = (itemIndex: number) => {
    setCohortDetailsList((prev) => {
      prev.splice(itemIndex, 1);
      return [...prev];
    });
  };

  return (
    <Modal style={{ zIndex: 500 }} open={open} onClose={handleClose}>
      <Grid container className={classes.root}>
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              fontSize="18px"
              lineHeight="26px"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.highemphasis}
              margin="0px 0px 4px 0px"
            >
              {title}
            </Typography>
          </Grid>
          <Grid style={{ height: '14px', width: '14px' }}>
            <Close
              height={'14px'}
              width={'14px'}
              fill={patientAccessTheme.palette.status.icon}
              onClick={handleClose}
              cursor={'pointer'}
            />
          </Grid>
        </Grid>

        {getSubtitle()}

        <Grid className={classes.mainFieldsContainer}>
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
            }}
          >
            {reportCreateNewLabelFields}
          </Grid>
          {showAddCohorts && (
            <Grid>
              <div className={classes.infoItem}>
                <div className={classes.addCohortInfo}>
                  <Info
                    height={'16px'}
                    width="16px"
                    style={{ marginRight: '8px', marginLeft: '10px' }}
                  />
                  <Typography
                    textColor={patientAccessTheme.palette.text.mediumemphasis}
                    fontSize="12px"
                    lineHeight="21.24px"
                    fontType="regular"
                  >
                    {isEditCohorts
                      ? constants.EDIT_COHORTS_INFO
                      : constants.ADD_COHORTS_INFO}
                  </Typography>
                  <Typography
                    textColor={patientAccessTheme.palette.primary[500]}
                    fontSize="12px"
                    lineHeight="21.24px"
                    fontType="medium"
                    className={classes.clickHere}
                    onClick={handleOnClickAddCohort}
                    cursor="pointer"
                  >
                    {constants.CLICK_HERE}
                  </Typography>
                </div>
              </div>
            </Grid>
          )}
          {infoGrid && <Grid>{infoGrid}</Grid>}
          {showDuration && (
            <Grid style={{ marginTop: '12px' }}>
              <NewEditDuration
                initialDates={initialDates}
                currentDates={currentDates}
                startDateUseState={startDateUseState!}
                endDateUseState={endDateUseState!}
                getAllApis={() => {}}
                showOnlyMainComponent={true}
                isYearGrid={isYearSelection}
                hideTitle={true}
                forCreateNewReports={true}
                mainComponentStyles={EditDurationStyles.mainComponent}
                dateContainerStyles={EditDurationStyles.dateContainerStyles}
                infoStyles={EditDurationStyles.infoStyles}
                resetDates={resetDates}
              />
            </Grid>
          )}
          {emailScheduler && <Grid>{emailScheduler}</Grid>}
        </Grid>
        <Grid
          style={{ display: cohortDetailsList.length ? 'initial' : 'none' }}
        >
          <Typography
            fontSize="12px"
            lineHeight="20px"
            fontType="semiBold"
            textColor={patientAccessTheme.palette.text.highemphasis}
            margin={'16px 0px 12px 0px'}
          >
            {constants.COHORT_DETAILS}
          </Typography>
        </Grid>

        <Grid
          container
          className={classes.cohortDetailsContainer}
          style={{ height: cohortDetailsList.length ? '178px' : '0px' }}
        >
          <ReportsCohortItem
            handleDeleteCohort={handleDeleteCohort}
            propertyDropDownOptions={propertyDropDownOptions}
            categoryDropDownOptions={categoryDropDownOptions}
            // subCategoryDropDownOptions={subCategoryDropDownOptions}
            conditionDropDownOptions={conditionDropDownOptions}
            cohortSelectionsValues={cohortSelectionsValues}
            totalResults={''}
            setPropertyDropDownOptions={function (
               
              value: React.SetStateAction<string[][]>,
            ): void {
              throw new Error('Function not implemented.');
            }}
          />
        </Grid>

        {/* remove this style for add cohort feature */}
        <Grid style={{ display: 'none' }}>
          <Typography
            fontSize="14px"
            lineHeight="28px"
            cursor="pointer"
            fontType="medium"
            textColor={patientAccessTheme.palette.primary[500]}
            margin={'12px 0px 0px 0px'}
            onClick={handleAddCohort}
          >
            {constants.ADD_COHORTS}
          </Typography>
        </Grid>

        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Grid style={{ marginRight: '12px' }}>
            <Button
              label={constants.CANCEL}
              variant="outlined"
              className={classes.cancelButton}
              onClick={handleClose}
            />
          </Grid>
          <Grid>
            <Button
              label={constants.SAVE}
              variant="contained"
              className={classes.saveButton}
              disabled={isSaveButtonDisabled}
              onClick={handleDownload}
            />
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default CreateNewModalCoreComponent;
